
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import Page from "@/layout/Page.vue";
import { EmailTemplate } from "@/store/models";
import { EmailTemplatesModule } from "@/store/modules/EmailTemplatesModule";
import { QuillEditor } from "@vueup/vue-quill";
import { ElMessage } from "element-plus";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { defineComponent, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";

export interface EmailVariable {
  tag: string;
  ayuda: string;
}

export default defineComponent({
  name: "email-templates-editor",
  components: { Page, QuillEditor },
  setup() {
    const store = useStore();
    const emailModule = getModule(EmailTemplatesModule, store);
    const { t } = useI18n();

    const guardando = ref(false);
    const cargando = ref(true);
    const selectedId = ref("");
    const listaLocal = ref<EmailTemplate[]>([]);

    const form = ref({
      nombre: "",
      asunto: "",
      cuerpoHtml: "",
      cuerpoTexto: "",
      variables: [] as EmailVariable[],
    });

    onMounted(async () => {
      setCurrentPageBreadcrumbs(t("menu.plantillasEmail"), [
        {
          title: t("menu.designContenido"),
          path: "/website/plantillas-email",
        },
      ]);

      try {
        Swal.fire({
          title: t("page.website.plantillasEmail.inicioAdvertenciaTitle"),
          html: t("page.website.plantillasEmail.inicioAdvertencia"),
          icon: "warning",
          buttonsStyling: false,
          width: "50%",
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
        // Aseguramos que cargamos las plantillas
        if (!emailModule.templates || emailModule.templates.length === 0) {
          await emailModule.fetchTemplates();
        }
        listaLocal.value = [...emailModule.templates];
      } catch (e) {
        console.error("Error cargando plantillas:", e);
      } finally {
        await nextTick();
        cargando.value = false;
      }
    });

    const loadTemplate = () => {
      const tpl = listaLocal.value.find(
        (t) => String(t.id) === selectedId.value
      );
      if (tpl) {
        form.value = JSON.parse(JSON.stringify(tpl));
      }
    };

    const copyTag = (tag: string) => {
      const text = `{{ ${tag} }}`;
      navigator.clipboard.writeText(text);
      ElMessage.success(`Copiado: ${text}`);
    };

    const handleSave = async () => {
      if (!selectedId.value) return;
      guardando.value = true;
      try {
        // 1. Guardamos los cambios en el servidor
        await emailModule.updateTemplate({
          id: selectedId.value,
          data: {
            asunto: form.value.asunto,
            cuerpoHtml: form.value.cuerpoHtml,
            cuerpoTexto: form.value.cuerpoTexto,
          },
        });

        // 2. Refrescamos el Store global para tener los datos actualizados
        await emailModule.fetchTemplates();

        // 3. Sincronizamos la lista local con el Store actualizado
        listaLocal.value = [...emailModule.templates];
        ElMessage.success(t("page.website.plantillasEmail.guardar.success"));
      } catch (err) {
        console.error("Error al guardar:", err);
        ElMessage.error(t("page.website.plantillasEmail.guardar.error"));
      } finally {
        guardando.value = false;
      }
    };

    return {
      listaLocal,
      selectedId,
      form,
      guardando,
      cargando,
      loadTemplate,
      handleSave,
      copyTag,
    };
  },
});
